// ייבוא מודולים בסיסיים של אנגולר
import { APP_INITIALIZER, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { ServiceWorkerModule } from '@angular/service-worker';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, UrlSerializer, DefaultUrlSerializer, UrlTree } from '@angular/router';

class LowerCaseUrlSerializer extends DefaultUrlSerializer {
  override parse(url: string): UrlTree {
    // EZ Type's connect page is the exception: its address carries the
    // desktop app's PKCE challenge and state, which are case-sensitive and
    // have to reach the server exactly as the app sent them. www.ezatwork.com
    // sends the path there in lower case already.
    if (url.startsWith('/eztype/')) {
      return super.parse(url);
    }
    return super.parse(url.toLowerCase());
  }
}
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

// ייבוא קומפוננטות ושירותים של האפליקציה
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ClientService } from './features/clients/services/client.service';
import { AuthService } from './features/auth/services/auth.service';
import { NotificationService } from './core/services/notification.service';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { PlanLimitInterceptor } from './core/interceptors/plan-limit.interceptor';

// Error Logging
import { GlobalErrorHandler } from './core/services/error-logging/global-error-handler.service';
import { HttpErrorInterceptor } from './core/services/error-logging/http-error.interceptor';

// ייבוא מודולים משותפים
import { NavModule } from './shared/components/nav/nav.module';
import { MaterialModule } from './shared/material/material.module';

// ייבוא מודולים לתרגום
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OfflineCacheInterceptor } from './core/offline/offline-cache.interceptor';
import { OfflineCacheStore } from './core/offline/offline-cache.store';
import { AppUpdateService } from './core/offline/app-update.service';
import { OfflineBannerComponent } from './shared/components/offline-banner/offline-banner.component';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslatedPaginatorIntl } from './core/i18n/translated-paginator-intl';

// ייבוא אנימציות
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { AddressAutocompleteComponent } from '@shared/components/address-autocomplete/address-autocomplete.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { FloatingTimerComponent } from '@shared/components/floating-timer/floating-timer.component';
import { ToastComponent } from '@core/components/toast/toast.component';
import { TourOverlayComponent } from '@shared/components/tour/tour-overlay.component';
import { FloatingNotesComponent } from '@features/notes/components/floating-notes/floating-notes.component';
import { AssistantLauncherComponent } from '@features/assistant/components/assistant-launcher/assistant-launcher.component';
import { GracePeriodBannerComponent } from '@shared/components/grace-period-banner/grace-period-banner.component';
import { AnnouncementBannerComponent } from '@shared/components/announcement-banner/announcement-banner.component';
import { ShellV2Component } from '@shared/components/shell-v2/shell-v2.component';
// פונקציית יצירת loader לתרגומים
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    // קומפוננטות נוספות שאינן מיובאות ממודולים אחרים
    AppComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    RouterModule.forRoot([]), // זה חשוב! זה מגדיר את התשתית הבסיסית לניתוב
    AppRoutingModule, // זה המודול שמכיל את הגדרות הניתוב הספציפיות שלך,
    NavModule,
    MaterialModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'en',
      useDefaultLang: true
    }),
    AddressAutocompleteComponent,
    FooterComponent,
    FloatingTimerComponent,
    TourOverlayComponent,
    FloatingNotesComponent,
    AssistantLauncherComponent,
    ToastComponent,
    GracePeriodBannerComponent,
    AnnouncementBannerComponent,
    ShellV2Component,
    OfflineBannerComponent,
    // The app shell and its files, so the app opens without a connection.
    // Not in development (ng serve), where it would serve stale builds.
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    { provide: UrlSerializer, useClass: LowerCaseUrlSerializer },
    // Localises every mat-paginator app-wide (10 list screens) — the default
    // intl is English-only and its range label reorders under RTL.
    { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },
    NotificationService,
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: (auth: AuthService) => () => auth.initSession(),
      deps: [AuthService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PlanLimitInterceptor,
      multi: true
    },
    // Last, next to the network: offline, a read gets the answer saved on this device.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfflineCacheInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (updates: AppUpdateService, store: OfflineCacheStore) => () => {
        updates.start();
        void store.prune();
      },
      deps: [AppUpdateService, OfflineCacheStore],
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    provideAnimationsAsync()
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
